// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { qtyFieldStyles } from "@guestsupply/App/Components/Qty/QtyField";
import cartLineSharedStyles from "@guestsupply/App/SharedStyles/CartLineSharedStyles";
import { color, hideElementOnPrint, mergeWithCss, tippySharedStyles, xScreen } from "@guestsupply/XDL/Utilities";
import { IconWrapper } from "@insite/mobius/Icon";
import getColor from "@insite/mobius/utilities/getColor";
import { css } from "styled-components";

// XNG-Changes: GSS-366
qtyFieldStyles.container = {
    css: css`
        display: flex;
        align-items: center;
        gap: 5px;
        ${xScreen("lte", "md")} {
            flex-direction: column;
            align-items: start;
            min-width: 100px;
        }
    `,
};
qtyFieldStyles.wrapper = {
    css: css`
        display: flex;
        flex-wrap: nowrap;
        ${hideElementOnPrint}
    `,
};
qtyFieldStyles.editableQuantityTextField = {
    labelProps: {
        variant: "legend",
        css: css`
            font-size: 10px;
            text-transform: uppercase;
            font-weight: 600;
        `,
    },
    cssOverrides: {
        inputSelect: css`
            color: ${getColor("text.main")};
            text-align: center;
            min-height: 28px;
            height: 28px;
            max-width: 55px;
            width: 55px;
            border-radius: 4px;
            ${hideElementOnPrint}
            &:focus {
                border-radius: 4px;
            }
            &.show-action-button {
                min-height: 33px;
                height: 33px;
                border-radius: 4px 0 0 4px;
                text-overflow: initial;
                padding: 0 4px;
                &:focus {
                    border-radius: 4px 0 0 4px;
                }
            }
        `,
    },
};
qtyFieldStyles.countButton = {
    variant: "tertiary",
    sizeVariant: "small",
    css: css`
        position: absolute;
        top: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 26px;
        height: 26px;
        flex: 0 0 26px;
        padding: 0;
        border: 0;
        z-index: 1;
        &:hover:not(:disabled) {
            ${IconWrapper} {
                color: ${color("common.hover")};
            }
        }
    `,
};
qtyFieldStyles.countIcon = {
    color: "common.ui5",
    size: 14,
    mergeCss: true,
};
qtyFieldStyles.valueText = {
    size: 16,
    weight: "bold",
    lineHeight: "17px",
    css: css`
        text-align: start;
        display: flex;
        line-height: 17px;
    `,
};
qtyFieldStyles.qtyLabel = {
    as: "label",
    size: 12,
    weight: "normal",
    lineHeight: "13px",
    css: css`
        margin-bottom: 4px;
        display: none;
        ${xScreen("lte", "md")} {
            display: block;
        }
        ${hideElementOnPrint}
    `,
};

qtyFieldStyles.decreaseButton = mergeWithCss(qtyFieldStyles.countButton, {
    css: css`
        left: 0;
    `,
});

qtyFieldStyles.increaseButton = mergeWithCss(qtyFieldStyles.countButton, {
    css: css`
        right: 0;
    `,
});
qtyFieldStyles.controlsWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        justify-content: space-between;
    `,
};
qtyFieldStyles.qtyClickable = {
    css: css`
        width: 18px;
        height: 16px;
        padding: 0px 4px;
        background-color: ${color("text")};

        &.plus {
            border-radius: 0 4px 0 0;
            margin-bottom: 1px;
        }
        &.minus {
            border-radius: 0 0 4px 0;
        }

        &:hover {
            opacity: 0.8;
        }

        &.disabled {
            opacity: 0.33;
        }

        &:active {
            background: ${color("common.ui6")};
        }
    `,
};
qtyFieldStyles.QuestionIconStyles = {
    color: "primary",
    height: 14,
    width: 14,
    css: css`
        opacity: 0.33;
        cursor: pointer;
        &:hover {
            color: ${getColor("common.ui8")};
        }
    `,
};

qtyFieldStyles.printQtyLabel = {
    ...mergeWithCss(cartLineSharedStyles.qtyLabel, {
        css: css`
            && {
                display: none;
                @media print {
                    margin: 0;
                    display: block;
                }
            }
        `,
    }),
};

qtyFieldStyles.printValueText = {
    ...mergeWithCss(cartLineSharedStyles.extendedUnitNetPriceText?.extendedStyles?.text, {
        as: "p",
        css: css`
            display: none;
            @media print {
                margin-bottom: 5px;
                display: block;
            }
        `,
    }),
};

qtyFieldStyles.multipleSaleQtyWrapper = {
    ...mergeWithCss(tippySharedStyles, {
        css: css`
            align-self: center;
            margin-left: 6px;
            span {
                margin: 0;
            }
        `,
    }),
};
